import { Button } from 'radiance-ui';
import React, { useEffect, useState } from 'react';

import APP_URLS from 'consts/appUrls';
import { BRAND, EVENTS } from 'consts/metricsTrackingEvents';
import { captureClickedCtaPageUrl } from 'utils/captureReferrer';
import { getFormattedLink } from 'utils/getFormattedLink';
import { sanitizeHexColor } from 'utils/hexColor';
import {
  navExternalWithQueryString,
  navInternalWithQueryString,
} from 'utils/marketingSafeDomain';

import {
  isAmplitudeInitialized,
  isAmplitudeLegacySdk,
} from 'utils/analytics/amplitude';
import { buttonContainerStyles } from './CtaButton.style';
import {
  getCtaButtonColorWarning,
  getCtaButtonTextColor,
} from './getColorWarning';

import { Modal } from 'components/global/modal';
import { ServicesModalDesktop } from 'components/global/Nav/components/ModalDesktop/components';
import { LOCAL_COLORS } from 'styles/constants';

export type ColorType = 'primary' | 'white' | 'dark-blue' | 'custom';

export type CtaButtonSize = 'Small' | 'Large';

export interface CtaButtonProps {
  buttonText: string;
  className?: string;
  color?: ColorType;
  /** HEX color for the button background/border. Only used when `color` is "custom". */
  customColor?: string;
  desktopSize?: CtaButtonSize;
  disclaimerText?: React.ReactNode;
  disclaimerLink?: string;
  isExternal?: boolean;
  link?: string;
  mobileSize?: CtaButtonSize;
  /** Used internally for analytics tracking. We should include this prop whenever this button is within a module. Follow camelCase convention. */
  module?: string;
  openServiceModal?: boolean;
  overrideClick?: () => void;
  small?: boolean;
  /** HEX color for the button label. Falls back to the default for the selected `color`. */
  textColor?: string;
}

export const CtaButton = ({
  buttonText,
  className,
  color = 'primary',
  customColor,
  desktopSize = 'Large',
  disclaimerText,
  disclaimerLink,
  isExternal,
  link,
  mobileSize = 'Large',
  module,
  openServiceModal,
  overrideClick,
  small = false,
  textColor,
  ...rest
}: CtaButtonProps) => {
  const [isModalHidden, setIsModalHidden] = useState(true);

  // Colors come straight from the CMS; only allow valid HEX codes into the stylesheet.
  const sanitizedCustomColor = sanitizeHexColor(customColor);

  // A custom background keeps Radiance's white label unless we say otherwise, which is
  // unreadable on a light color. Fall back to whichever default label color contrasts better.
  const sanitizedTextColor = getCtaButtonTextColor(
    color,
    sanitizedCustomColor,
    sanitizeHexColor(textColor),
  );

  // Surfaces color combinations an editor can set in Contentful but shouldn't.
  // In a dependency-gated effect so it logs once per combination rather than on
  // every render.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const warning = getCtaButtonColorWarning({
      buttonText,
      color,
      customColor,
      textColor,
    });

    if (warning) console.warn(warning);
  }, [buttonText, color, customColor, textColor]);

  const handleModalClose = () => {
    setIsModalHidden(true);
  };

  // Use the isExternal override unless the link is obviously an external link
  // so that Gatsby doesn't try to preload it and cause a 404 error in the console.
  const linkIsExternal = isExternal || link?.match(/^(http|https):\/\//);
  const appUrl = APP_URLS.welcome;
  const formattedLink = link && getFormattedLink(link);
  const redirect = () => {
    if (link && linkIsExternal) navExternalWithQueryString(link);
    else if (formattedLink) navInternalWithQueryString(formattedLink);
    else navExternalWithQueryString(appUrl);
  };

  const handleClick = () => {
    const event = formattedLink ? EVENTS.linkClicked : EVENTS.ctaClicked;
    const payload = {
      path: window.location.pathname,
      url: window.location.href,
      module,
      brand: BRAND,
    };

    let ctaAction = redirect;

    if (overrideClick) {
      ctaAction = () => overrideClick();
    } else if (openServiceModal) {
      ctaAction = () => setIsModalHidden(false);
    }

    if (isAmplitudeInitialized()) {
      isAmplitudeLegacySdk()
        ? window.amplitude.getInstance().logEvent(event, payload, ctaAction)
        : window.amplitude.track(event, payload);
    }

    captureClickedCtaPageUrl();

    ctaAction();
  };

  return (
    <React.Fragment>
      {!isModalHidden && (
        <Modal
          children={<ServicesModalDesktop module={''} />}
          backgroundColor={LOCAL_COLORS.surfaceWhite}
          onClose={handleModalClose}
        />
      )}
      <div
        className={className}
        css={buttonContainerStyles({
          color,
          customColor: sanitizedCustomColor,
          desktopSize,
          mobileSize,
          small,
          textColor: sanitizedTextColor,
        })}
      >
        <Button isFullWidth onClick={handleClick} {...rest}>
          {buttonText}
        </Button>
        {disclaimerText && <small>{disclaimerText}</small>}
        {disclaimerLink && (
          <small
            dangerouslySetInnerHTML={{
              __html: disclaimerLink,
            }}
          />
        )}
      </div>
    </React.Fragment>
  );
};
